// Opt-in, bounded encounter-control diagnostics stored on FateTide.
import { encounterField } from '../shared/array-types.js';

type Snapshot = { inCombat: number | null; activeCombatId: string | null; version: number | null; turn: string | null };
type TraceRow = { sequence: number; at: number; source: string; encounterId: string; meta: Record<string, unknown>; before: Snapshot; after: Snapshot; incoming: Snapshot | null; lostCombat: boolean };
type TraceStore = { encounterTruthTraceEnabled?: boolean; encounterTruthTrace?: TraceRow[]; encounterTruthLosses?: TraceRow[] };

function snapshot(encounter: unknown): Snapshot {
  const flag = encounterField<unknown>(encounter, 'in_combat');
  const version = (encounter as { _ws_version?: unknown } | null)?._ws_version;
  return {
    inCombat: flag === undefined || flag === '' ? null : Number(flag),
    activeCombatId: encounterField<string>(encounter, 'active_combat_id') ?? null,
    version: typeof version === 'number' ? version : null,
    turn: encounterField<string>(encounter, 'current_encounter_creature_id_turn') ?? null,
  };
}

/** Record at entry AND completion: overlapping async adopters retain their start order and their actual install order. */
export function traceEncounterTruth(source: string, encounterId: string, meta: Record<string, unknown> = {}, incoming?: unknown): (() => void) | undefined {
  try {
    if (typeof window === 'undefined') return;
    const store = window.FateTide as TraceStore | undefined;
    if (store?.encounterTruthTraceEnabled !== true) return;
    const rows = store.encounterTruthTrace ??= [];
    const readHeld = (): Snapshot => snapshot(window.FateTide?.encounterCache?.get(encounterId));
    const before = readHeld(), proposed = incoming ? snapshot(incoming) : null;
    const push = (after: Snapshot, phase: string): void => {
      const row = { sequence: (rows[rows.length - 1]?.sequence ?? 0) + 1, at: Date.now(), source, encounterId, meta: { ...meta, phase }, before, after, incoming: proposed,
        lostCombat: (before.inCombat === 1 || !!before.activeCombatId) && after.inCombat !== 1 && !after.activeCombatId };
      rows.push(row);
      if (rows.length > 256) rows.splice(0, rows.length - 256);
      if (row.lostCombat) {
        const losses = store.encounterTruthLosses ??= [];
        losses.push(row);
        if (losses.length > 8) losses.shift();
      }
    };
    push(before, 'enter');
    return () => {
      try { if (store.encounterTruthTraceEnabled === true) push(readHeld(), 'applied'); }
      catch { /* Diagnostics must not interrupt frame application. */ }
    };
  } catch { /* Diagnostics must not interrupt frame application. */ }
}

// Legacy template.js uses this bridge, never a second stateful module instance.
try { if (typeof window !== 'undefined') Object.assign(window, { ftTraceEncounterTruth: traceEncounterTruth }); }
catch { /* A missing diagnostic bridge must not prevent module import. */ }
